import ExerciseRunner from './ExerciseRunner.jsx';
import SpeechButton from './SpeechButton.jsx';
import { getBestScore, scorePercent } from '../lib/progress.js';

const MODE_META = {
  'guided-practice': { emoji: '📘', label: 'Guided Practice', short: 'Guided', className: 'guided' },
  'sentence-building': { emoji: '✍️', label: 'Sentence Building', short: 'Building', className: 'building' },
  'listen-spell': { emoji: '🎧', label: 'Listen & Spell', short: 'Spell', className: 'spell' },
  'listen-for-details': { emoji: '🔎', label: 'Listen for Details', short: 'Details', className: 'details' },
};

export default function PracticeWorkspace({
  theme,
  subtopic,
  headword,
  content,
  progress,
  activeMode,
  onModeChange,
  rounds,
  generatingMode,
  generationError,
  onGenerate,
  onPrepareGuidedPassage,
  onComplete,
  onExit,
}) {
  const modes = headword
    ? ['guided-practice', 'sentence-building']
    : ['listen-spell', 'listen-for-details'];
  const scores = Object.fromEntries(modes.map((mode) => [mode, getBestScore(progress, {
    themeId: theme.id,
    subtopicId: subtopic.id,
    headwordId: headword?.id,
    mode,
  })]));

  return (
    <main className="page workspace-page">
      <button className="back-button" type="button" onClick={onExit}>← Back to {headword ? 'headwords' : 'subtopics'}</button>

      <div className="workspace-grid">
        <aside className="workspace-aside">
          <section className="compact-context">
            <span>CHOSEN</span>
            <strong>{theme.title}</strong>
            {headword && <><i>›</i><strong>{subtopic.title}</strong></>}
          </section>
          {headword ? (
            <section className="selected-headword">
              <div className="selected-headword-top">
                <div>
                  <small>SELECTED HEADWORD</small>
                  <span className="selected-word"><strong>{headword.displayText}</strong><SpeechButton text={headword.audio?.speechText ?? headword.displayText} /></span>
                  <span>{headword.partOfSpeech}</span>
                </div>
                <b>Selected</b>
              </div>
              <p lang="zh-Hant">{headword.definitionZh}</p>
              <div className="dual-gauge dual-gauge-vocabulary">
                <span style={{ width: `${scorePercent(scores['guided-practice']) / 2}%` }} />
                <span style={{ width: `${scorePercent(scores['sentence-building']) / 2}%` }} />
              </div>
              <div className="dual-gauge-labels">
                <span>{scores['guided-practice']?.score ?? 0}/10 guided</span>
                <span>{Math.floor((scores['sentence-building']?.score ?? 0) / 3)}/5 sentences</span>
              </div>
            </section>
          ) : (
            <section className="selected-headword selected-subtopic">
              <div className="selected-headword-top">
                <div>
                  <small>SELECTED SUBTOPIC</small>
                  <span className="selected-word"><strong>{subtopic.title}</strong></span>
                  {subtopic.titleZh && <span lang="zh-Hant">{subtopic.titleZh}</span>}
                </div>
                <b>Selected</b>
              </div>
              <div className="dual-gauge dual-gauge-listening">
                <span style={{ width: `${scorePercent(scores['listen-spell']) / 2}%` }} />
                <span style={{ width: `${scorePercent(scores['listen-for-details']) / 2}%` }} />
              </div>
              <div className="dual-gauge-labels">
                <span>{scores['listen-spell']?.score ?? 0}/10 spell</span>
                <span>{scores['listen-for-details']?.score ?? 0}/15 details</span>
              </div>
            </section>
          )}
          <div className="workspace-legend">
            {headword ? (
              <><span><i className="legend-dot guided-dot" /> Guided</span><span><i className="legend-dot building-dot" /> Sentence Building</span></>
            ) : (
              <><span><i className="legend-dot spell-dot" /> Listen & Spell</span><span><i className="legend-dot details-dot" /> Listen for Details</span></>
            )}
          </div>
        </aside>

        <section className="workspace-main">
          <div className="mode-tabs" role="tablist" aria-label="Learning mode">
            {modes.map((mode) => {
              const meta = MODE_META[mode];
              return (
                <button
                  className={`mode-tab mode-tab-${meta.className} ${activeMode === mode ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  aria-selected={activeMode === mode}
                  key={mode}
                  onClick={() => onModeChange(mode)}
                >
                  <span aria-hidden="true">{meta.emoji}</span>{meta.label}
                </button>
              );
            })}
          </div>
          {generationError && <div className="generation-error" role="alert">⚠️ {generationError}</div>}
          <div className="workspace-panel">
            {modes.map((mode) => (
              <div key={mode} hidden={activeMode !== mode}>
                <ExerciseRunner
                  key={`${mode}-${rounds[mode]?.key ?? 'stored'}`}
                  mode={mode}
                  theme={theme}
                  subtopic={subtopic}
                  headword={headword}
                  content={content}
                  round={rounds[mode]}
                  onComplete={(result) => onComplete(mode, result)}
                  onGenerate={() => onGenerate(mode)}
                  onPreparePassage={onPrepareGuidedPassage}
                  generating={generatingMode === mode}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
